import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import OvRecordEditDialog from './OvRecordEditDialog';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { DeleteVideoAction } from '../../../common/model/type/video-placement.types';
import {
  getDeleteConfirmation,
  isSameDeleteConfirmation,
} from '../../../common/utils/video-placement-utils';
import { ADMIN_VIDEO_KEY } from '../../../common/utils/video-query-cache';
import { getVideo } from '../../../services/admin/admin-video.service';
import { useDeleteVideo } from '../../../hooks/use-delete-video';
import { useVideoPlacements } from '../../../hooks/use-video-placements';
import { useVideoUploads } from '../../../hooks/use-video-uploads';
import OvDeleteVideoConsequences from './OvDeleteVideoConsequences';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvDeleteVideoDialogProps {
  video: AdminVideo;
  onClose: () => void;
  onDeleted: (action: DeleteVideoAction) => void;
}

const OvDeleteVideoDialog: FC<OvDeleteVideoDialogProps> = ({
  video,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [openedWith] = useState(video);
  const placements = useVideoPlacements().data ?? [];
  const { uploads } = useVideoUploads();
  const { deleteVideo, isDeleting, error } = useDeleteVideo();
  const { data: current = video } = useQuery({
    queryKey: [ADMIN_VIDEO_KEY, video.id],
    queryFn: () => getVideo(video.id),
    refetchOnMount: 'always',
  });

  const upload = uploads.find(({ videoId }) => videoId === video.id);
  const confirmation = getDeleteConfirmation(current, placements, upload);
  const hasChanged = !isSameDeleteConfirmation(
    getDeleteConfirmation(openedWith, placements, upload),
    confirmation
  );
  const { action } = confirmation;

  return (
    <OvRecordEditDialog
      isOpen
      title={t(`videos.delete.${action}Title`)}
      onClose={onClose}
      onSubmit={() => deleteVideo(video.id, () => onDeleted(action))}
      isSaving={isDeleting}
      errorMessage={error ? t(`videos.delete.${action}Failed`) : undefined}
      submitLabel={t(`videos.delete.${action}Action`)}
      busyLabel={t(
        action === 'delete'
          ? 'videos.delete.deleting'
          : 'videos.delete.archiving'
      )}
      submitTone="danger"
      isCancelFocused
      maxWidth="xs"
    >
      <Target>
        <strong>{current.title}</strong>
        <span>{t('videos.delete.videoId', { id: current.id })}</span>
      </Target>
      {hasChanged && (
        <Changed role="alert">{t('videos.delete.changed')}</Changed>
      )}
      <OvDeleteVideoConsequences confirmation={confirmation} />
    </OvRecordEditDialog>
  );
};

export default OvDeleteVideoDialog;

const Target = styled.p`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  overflow-wrap: anywhere;

  span {
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
  }
`;

const Changed = styled.p`
  margin: 0;
  font-weight: bold;
`;
